import { useState, useEffect } from 'react';
import { get } from 'api/GroupController';
import errorReporter from '@app/errorReporter';
import type { UserRoles } from '@bb/users';
import { useDebounce } from 'hooks';
import { message } from 'components';
import useIsBranchAdmin from './useIsBranchAdmin';
import type { GroupMember } from '../util';
import { compareFirstAndLastName } from '../util';

export interface GroupInfo {
  id: string;
  name: string;
  createdAt: string;
  org: string;
  updatedAt: string;
  users: GroupMember[];
}

const useGetGroupMembers = ({
  groupId,
  searchText,
}: {
  groupId: string;
  searchText: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [groupInfo, setGroupInfo] = useState<GroupInfo | undefined>(undefined);
  const debouncedSearchText = useDebounce(searchText, 500);
  const [unfilteredUsers, setUnfilteredUsers] = useState<GroupMember[]>([]);
  const { isBranchAdmin, branchAdminCanEdit } = useIsBranchAdmin();

  const fetchGroupMembers = async (active: boolean) => {
    try {
      setIsLoading(true);
      const memberList: GroupInfo = await get(groupId);
      if (!active) return;
      const users = memberList?.users?.filter(
        user =>
          !isBranchAdmin ||
          branchAdminCanEdit({
            ...user,
            role: user.role as UserRoles,
          }),
      );
      users.sort(compareFirstAndLastName);
      setGroupInfo({ ...memberList, users });
      setUnfilteredUsers(users || []);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error loading group members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!groupId) return setGroupInfo(undefined);
    let active = true;

    fetchGroupMembers(active);
    return () => {
      active = false;
    };
  }, [groupId]);

  useEffect(() => {
    setGroupInfo(info =>
      info
        ? {
            ...info,
            users: unfilteredUsers.filter(({ firstName, lastName }) =>
              `${firstName} ${lastName}`
                .toLowerCase()
                .includes(debouncedSearchText.toLowerCase()),
            ),
          }
        : undefined,
    );
  }, [debouncedSearchText, unfilteredUsers]);

  return {
    isLoading,
    groupInfo,
    fetchGroupMembers,
    groupMembersCount: groupInfo?.users?.length || 0,
    unfilteredUsers,
  };
};

export default useGetGroupMembers;
